import React, { useState, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { useParams, Link } from 'react-router-dom'
import { useFetch, usePost, useClick, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { TabMenu } from '../../../../components/component-items/tab-menu'
import CardLoading from '../../../../components/component-items/loading-popover'
import GenericModal from '../../../../components/component-items/modal'
import { PermissionGate } from '../../../../components/rbac'
import { DetailHeader, DetailInfo, ActionsDropdown, TabContent } from '../styles'

// Tabs
import { ContentsTab } from './contents-tab'
import { ChildrenTab } from './children-tab'
import { HistoryTab } from './history-tab'

// Modals (lazy loaded)
const MoveContainerModal = React.lazy(() => import('../modals/move-container-modal'))
const PrintLabelModal = React.lazy(() => import('../modals/print-label-modal'))

// Types
import type { LogisticUnitDetail } from '../types'
import { STATE_COLOR_MAP, STATE_DISPLAY_MAP } from '../types'

export const ContainerDetail = () => {
  const { lpn } = useParams<{ lpn: string }>()
  const {
    state: { csrf },
  } = globalState()

  // Tabs
  const tabOptions = ['Contents', 'Children', 'History']
  const [activeTab, setActiveTab] = useState(tabOptions[0])

  // Actions dropdown
  const [actionsOpen, setActionsOpen] = useState(false)
  const actionsNode = useRef<HTMLDivElement | null>(null)
  useClick(actionsNode, setActionsOpen)

  // Modals
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false)
  const [showNestModal, setShowNestModal] = useState(false)
  const [nestParentLpn, setNestParentLpn] = useState('')
  const [nestSubmitting, setNestSubmitting] = useState(false)

  // Fetch container detail
  const url = `/api/inventory/logistic-unit/${lpn}/`
  const res: any = useFetch(url, {})
  // ──────────────────────────────────────────────────────────────────────────
  // Validate response shape — API may return an error object (e.g. {detail: ...}) which is
  // truthy but has no contents/children, causing tab components to crash.
  const rawResponse = res.loaded ? res.response : null
  const container: LogisticUnitDetail | null =
    rawResponse && typeof rawResponse === 'object' && 'lpn' in rawResponse ? rawResponse : null

  const handleUnnest = async () => {
    if (window.confirm(`Remove this container from its parent (${container?.parent_lpn})?`)) {
      await usePost(`/api/inventory/logistic-unit/${lpn}/unnest/`, {}, csrf)
    }
  }

  const handleRetire = async () => {
    if (window.confirm('Are you sure you want to retire this container? This cannot be undone.')) {
      await usePost(`/api/inventory/logistic-unit/${lpn}/retire/`, {}, csrf)
    }
  }

  return (
    <GlobalStyles.FullPageCard>
      <DetailHeader>
        <DetailInfo>
          <div style={{ marginBottom: '0.5rem' }}>
            <Link to="/inventory/container/" style={{ color: '#5d78ff', fontSize: '0.85rem' }}>
              &larr; Back to Containers
            </Link>
          </div>
          <h3>LPN: {lpn}</h3>
          {container && (
            <div>
              <span>Type: {container.container_type_name || '\u2014'}</span>
              <span>
                State:{' '}
                <StatusBoxStyled
                  className={STATE_COLOR_MAP[container.state] || 'silver'}
                  style={{ display: 'inline-block', padding: '0.25rem 0.75rem' }}
                >
                  {STATE_DISPLAY_MAP[container.state] || container.state}
                </StatusBoxStyled>
              </span>
              <span>Location: {container.location_barcode}</span>
              {container.parent_lpn && (
                <span>
                  Parent: <Link to={`/inventory/container/${container.parent_lpn}/`}>{container.parent_lpn}</Link>
                </span>
              )}
            </div>
          )}
        </DetailInfo>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <GlobalStyles.Button className="secondary" onClick={() => setShowPrintLabelModal(true)}>
            Print Label
          </GlobalStyles.Button>
          <PermissionGate segment="inventory" minLevel="operator">
            <ActionsDropdown ref={actionsNode}>
              <GlobalStyles.Button className="secondary" onClick={() => setActionsOpen(!actionsOpen)}>
                Actions &#9662;
              </GlobalStyles.Button>
              {actionsOpen && (
                <ul>
                  <li
                    onClick={() => {
                      setShowMoveModal(true)
                      setActionsOpen(false)
                    }}
                  >
                    Move Container
                  </li>
                  {container && !container.parent_lpn && (
                    <li
                      onClick={() => {
                        setShowNestModal(true)
                        setActionsOpen(false)
                      }}
                    >
                      Nest into Parent
                    </li>
                  )}
                  {container && container.parent_lpn && (
                    <li
                      onClick={() => {
                        handleUnnest()
                        setActionsOpen(false)
                      }}
                    >
                      Remove from Parent
                    </li>
                  )}
                  <PermissionGate segment="inventory" minLevel="admin">
                    <li
                      className="danger"
                      onClick={() => {
                        handleRetire()
                        setActionsOpen(false)
                      }}
                    >
                      Retire Container
                    </li>
                  </PermissionGate>
                </ul>
              )}
            </ActionsDropdown>
          </PermissionGate>
        </div>
      </DetailHeader>

      <TabContent>
        <TabMenu {...{ tabOptions, activeTab, setActiveTab }} />
        <div style={{ marginTop: '1.5rem' }}>
          {container && activeTab === 'Contents' && (
            <ContentsTab
              lpn={lpn!}
              contents={container.contents}
              totalQuantity={container.total_quantity}
              contentsCount={container.contents_count}
            />
          )}
          {container && activeTab === 'Children' && <ChildrenTab children={container.children} />}
          {activeTab === 'History' && lpn && <HistoryTab lpn={lpn} />}
        </div>
      </TabContent>

      {!res.loaded ? <CardLoading text={res.placeholder} error={res.error} /> : null}
      {res.loaded && !container && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#fd397a' }}>
          Container not found or invalid response for LPN: {lpn}
        </div>
      )}

      {showMoveModal && lpn && (
        <React.Suspense fallback={null}>
          <MoveContainerModal lpn={lpn} show={showMoveModal} onHide={() => setShowMoveModal(false)} />
        </React.Suspense>
      )}
      {showPrintLabelModal && lpn && (
        <React.Suspense fallback={null}>
          <PrintLabelModal lpns={[lpn]} show={showPrintLabelModal} onHide={() => setShowPrintLabelModal(false)} />
        </React.Suspense>
      )}
      {showNestModal && lpn && (
        <GenericModal
          heading={`Nest ${lpn} into Parent`}
          show={showNestModal}
          onHide={() => {
            setShowNestModal(false)
            setNestParentLpn('')
          }}
          buttons={
            <GlobalStyles.Button
              style={{ minWidth: 125 }}
              onClick={async () => {
                if (!nestParentLpn) return
                setNestSubmitting(true)
                try {
                  const reload = false
                  const stringify = true
                  const sendResponse = false
                  const response = await usePost(
                    `/api/inventory/logistic-unit/${lpn}/nest/`,
                    { parent_lpn: nestParentLpn },
                    csrf,
                    reload,
                    stringify,
                    sendResponse
                  )
                  if (!response) return
                  if (!response.error) {
                    notify({ type: 'success', message: `${lpn} nested into ${nestParentLpn}` })
                    setShowNestModal(false)
                    setNestParentLpn('')
                    window.location.reload()
                  }
                } finally {
                  setNestSubmitting(false)
                }
              }}
              disabled={!nestParentLpn || nestSubmitting}
            >
              {nestSubmitting ? 'Nesting...' : 'Nest Container'}
            </GlobalStyles.Button>
          }
        >
          <div style={{ padding: '0.5rem 0' }}>
            <Form.Group controlId="nestParentLpn">
              <Form.Label>
                Parent LPN <span style={{ color: '#fd397a' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={nestParentLpn}
                onChange={(e) => setNestParentLpn(e.target.value)}
                placeholder="Enter parent container LPN"
              />
            </Form.Group>
          </div>
        </GenericModal>
      )}
    </GlobalStyles.FullPageCard>
  )
}
